import { useEffect, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { db, mutate } from '@/runtime'
import { useProjects, useSettings, useTasks } from '@/db/hooks'
import { META } from '@/db/schema'
import type { TaskResource } from '@/kinds/task'
import type { ProjectResource } from '@/kinds/project'
import type { SettingsSpec } from '@/kinds/settings'
import { taskActions } from '@/lib/actions'
import { completionsThisWeek, planReview, type ReviewSection } from '@/lib/review'
import { addDays, todayLocal } from '@/lib/time'
import { useUi } from '@/state/ui'
import { TaskMeta } from '@/components/TaskRow'
import { Button, Field, PageTitle } from '@/components/ui'
import { AppTabs, REVIEW_TABS } from '@/components/AppTabs'

const SECTIONS: { key: ReviewSection; title: string; blurb: string }[] = [
  { key: 'slipped', title: 'Slipped', blurb: 'These went past their deadline. No judgement — just decide.' },
  { key: 'stale_inbox', title: 'Stale inbox', blurb: 'Captured a while ago and never sorted.' },
  { key: 'stale_next', title: 'Stale next', blurb: 'Marked next, never scheduled, untouched for a while.' },
  { key: 'waiting', title: 'Waiting', blurb: 'Waiting on someone or something.' },
  { key: 'projects', title: 'Projects', blurb: 'Active projects with no open task.' },
]

const Dots = ({ n, i }: { n: number; i: number }) => (
  <div className="flex gap-1 justify-center my-3" aria-label={`item ${i + 1} of ${n}`}>
    {Array.from({ length: Math.min(n, 30) }, (_, k) => (
      <span
        key={k}
        className={`h-1.5 w-1.5 rounded-full ${k < i ? 'bg-accent' : k === i ? 'bg-fg' : 'bg-raised'}`}
      />
    ))}
  </div>
)

/** Waits for Dexie so the wizard can freeze its plan on first render. */
export const ReviewView = () => {
  const tasks = useTasks()
  const projects = useProjects()
  const settings = useSettings()
  if (!tasks || !projects) return null
  return <Wizard tasks={tasks} projects={projects} settings={settings} />
}

/** One item at a time, big buttons, escape at any point (spec §8.5). */
const Wizard = ({
  tasks,
  projects,
  settings,
}: {
  tasks: TaskResource[]
  projects: ProjectResource[]
  settings: SettingsSpec
}) => {
  const navigate = useNavigate()
  const today = todayLocal()
  // freeze the plan at the start so acting on an item doesn't reshuffle the wizard
  const [plan] = useState(() => planReview(tasks, projects, settings, today))
  const [section, setSection] = useState(0)
  const [idx, setIdx] = useState(0)
  const [date, setDate] = useState(addDays(today, 1))
  const done = completionsThisWeek(tasks)
  const setQuickAdd = useUi((s) => s.setQuickAdd)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void navigate({ to: '/' })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  const current = section < SECTIONS.length ? SECTIONS[section]! : null
  const items: (TaskResource | ProjectResource)[] = current ? plan[current.key] : []
  const item = items[idx]

  const advance = () => {
    if (idx + 1 < items.length) setIdx(idx + 1)
    else {
      setIdx(0)
      setSection(section + 1)
    }
  }
  // skip empty sections
  if (current && items.length === 0) {
    setSection(section + 1)
    return null
  }

  if (!current) {
    void db.setMeta(META.lastReviewAt, new Date().toISOString())
    return (
      <div className="fade-in">
        <PageTitle>Review</PageTitle>
        <AppTabs tabs={REVIEW_TABS} label="Review" />
        {/* no tally here: what got done is counted in the Log, the one place with numbers */}
        <div className="text-center py-10">
          <p className="text-lg mb-1">That's the review done.</p>
          <p className="text-muted text-sm mb-6">Nothing else is asking for a decision this week.</p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <Link to="/" className="underline">
              Back to Today
            </Link>
            {done.length > 0 && (
              <Link to="/log" className="underline text-muted">
                What got done
              </Link>
            )}
          </div>
        </div>
      </div>
    )
  }

  const byId = new Map(tasks.map((t) => [t.id, t]))
  const act = async (fn: () => Promise<unknown>) => {
    await fn()
    advance()
  }
  const t = item as TaskResource
  const p = item as ProjectResource
  const isProject = current.key === 'projects'

  return (
    <div className="fade-in" id={current.key}>
      <PageTitle>Review</PageTitle>
      <AppTabs tabs={REVIEW_TABS} label="Review" />
      <div className="flex items-center justify-between">
        <h2 className="t-title2">{current.title}</h2>
        {/* the key's hint only where there is a key to press */}
        <Link to="/" className="pointer-coarse:hidden text-xs text-faint min-h-8 flex items-center">
          <kbd className="font-sans rounded border border-line px-1.5">esc</kbd>
        </Link>
      </div>
      <p className="text-sm text-muted mb-2">{current.blurb}</p>
      <Dots n={items.length} i={idx} />
      <div className="card p-4 mb-4 !bg-raised">
        <div className="text-lg font-medium">{isProject ? p.spec.title : t.spec.title}</div>
        {!isProject && <TaskMeta task={t} />}
        {!isProject && t.spec.notes && (
          <p className="text-sm text-muted mt-2 whitespace-pre-wrap">{t.spec.notes}</p>
        )}
        {isProject && p.spec.outcome && <p className="text-sm text-muted mt-2">{p.spec.outcome}</p>}
      </div>

      {current.key === 'slipped' && (
        <div className="grid grid-cols-2 gap-2">
          <div className="col-span-2 flex gap-2 items-end">
            <Field label="Reschedule to">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </Field>
            <Button
              big
              variant="primary"
              className="mb-3 flex-1"
              onClick={() => void act(() => taskActions.defer(t, date))}
            >
              Reschedule
            </Button>
          </div>
          <Button big onClick={() => void act(() => taskActions.move(t, 'someday'))}>
            Someday
          </Button>
          <Button big onClick={() => void act(() => taskActions.drop(t))}>
            Drop
          </Button>
          <Button big className="col-span-2" onClick={() => void act(() => taskActions.complete(t))}>
            Actually, done
          </Button>
        </div>
      )}
      {(current.key === 'stale_inbox' || current.key === 'stale_next') && (
        <div className="grid grid-cols-2 gap-2">
          <Button big variant="primary" onClick={() => void act(() => taskActions.move(t, 'next'))}>
            Next
          </Button>
          <Button big onClick={() => void act(() => taskActions.move(t, 'someday'))}>
            Someday
          </Button>
          <Button big onClick={() => void act(() => taskActions.drop(t))}>
            Drop
          </Button>
          <Button big onClick={() => void act(() => taskActions.touch(t))}>
            Keep
          </Button>
        </div>
      )}
      {current.key === 'waiting' && (
        <div className="grid grid-cols-2 gap-2">
          <Button big onClick={() => void act(() => taskActions.touch(t))}>
            Still waiting
          </Button>
          <Button big variant="primary" onClick={() => void act(() => taskActions.move(t, 'next'))}>
            Unblocked
          </Button>
          <Button big className="col-span-2" onClick={() => void act(() => taskActions.drop(t))}>
            Drop
          </Button>
        </div>
      )}
      {isProject && (
        <div className="grid grid-cols-2 gap-2">
          <Button
            big
            variant="primary"
            className="col-span-2"
            onClick={() => {
              void navigate({ to: '/projects/$id', params: { id: p.id } })
              setTimeout(() => setQuickAdd(true), 50)
            }}
          >
            Add next task
          </Button>
          <Button big onClick={() => void act(() => mutate.action(p.id, 'pause'))}>
            Pause
          </Button>
          <Button big onClick={() => void act(() => mutate.action(p.id, 'complete'))}>
            Done
          </Button>
          <Button big className="col-span-2" onClick={advance}>
            Skip
          </Button>
        </div>
      )}
      {!isProject && byId.get(t.id)?.status.state === 'done' && (
        <p className="text-xs text-faint mt-2">Already completed elsewhere.</p>
      )}
    </div>
  )
}
